// Mappers for machine-related entities
// Identity mappers for 1:1 mapping

import type {
  MachineDto,
  MachineGroupDto,
  MachineEventDto,
  MachineBrandDto,
  MachineConnectionMethodDto,
  MachineStatsHourDto,
  MachineStatsDayDto,
  MachineStatsMonthDto,
  MachineStatsWeekDto,
  MachineStatsHourAllDto,
  MachineStatsDayAllDto,
  MachineStatsMonthAllDto,
  MachineStatsWeekAllDto,
  NcFileStatsHourDto,
  NcFileStatsDayDto,
  NcFileStatsMonthDto,
  MachineAlarmEventDto,
  OperationDto,
  DemoMachineTypeDto,
  AvailableMachinesResponseDto,
  ConfigureOperationResponseDto,
  ConfigureMachineGroupResponseDto,
  MachineReferencesResponseDto,
  ConfigureMachineReferencesResponseDto,
  RetrieveRawMachineEventDataResponseDto,
  MachineCountingProfileDto,
} from '../../generated/models';

import type {
  Machine,
  MachineGroup,
  MachineEvent,
  MachineBrand,
  MachineConnectionMethod,
  MachineAlarmEvent,
  Operation,
  DemoMachineType,
  MachineStatsHour,
  MachineStatsDay,
  MachineStatsMonth,
  MachineStatsWeek,
  MachineStatsHourAll,
  MachineStatsDayAll,
  MachineStatsMonthAll,
  MachineStatsWeekAll,
  NcFileStatsHour,
  NcFileStatsDay,
  NcFileStatsMonth,
  AvailableMachinesResponse,
  ConfigureOperationResponse,
  ConfigureMachineGroupResponse,
  MachineReferencesResponse,
  ConfigureMachineReferencesResponse,
  RetrieveRawMachineEventDataResponse,
  MachineCountingProfile,
} from '../types';

// Entity mappers (identity for 1:1)
export const toMachine = (dto: MachineDto): Machine => dto;
export const toMachines = (dtos: MachineDto[]): Machine[] => dtos.map(toMachine);

export const toMachineGroup = (dto: MachineGroupDto): MachineGroup => dto;
export const toMachineGroups = (dtos: MachineGroupDto[]): MachineGroup[] => dtos.map(toMachineGroup);

export const toMachineEvent = (dto: MachineEventDto): MachineEvent => dto;
export const toMachineEvents = (dtos: MachineEventDto[]): MachineEvent[] => dtos.map(toMachineEvent);

export const toMachineBrand = (dto: MachineBrandDto): MachineBrand => dto;
export const toMachineBrands = (dtos: MachineBrandDto[]): MachineBrand[] => dtos.map(toMachineBrand);

export const toMachineConnectionMethod = (dto: MachineConnectionMethodDto): MachineConnectionMethod => dto;
export const toMachineConnectionMethods = (dtos: MachineConnectionMethodDto[]): MachineConnectionMethod[] =>
  dtos.map(toMachineConnectionMethod);

export const toMachineAlarmEvent = (dto: MachineAlarmEventDto): MachineAlarmEvent => dto;
export const toMachineAlarmEvents = (dtos: MachineAlarmEventDto[]): MachineAlarmEvent[] =>
  dtos.map(toMachineAlarmEvent);

export const toOperation = (dto: OperationDto): Operation => dto;
export const toOperations = (dtos: OperationDto[]): Operation[] => dtos.map(toOperation);

export const toDemoMachineType = (dto: DemoMachineTypeDto): DemoMachineType => dto;
export const toDemoMachineTypes = (dtos: DemoMachineTypeDto[]): DemoMachineType[] => dtos.map(toDemoMachineType);

// Stats mappers (identity for 1:1)
export const toMachineStatsHour = (dto: MachineStatsHourDto): MachineStatsHour => dto;
export const toMachineStatsHours = (dtos: MachineStatsHourDto[]): MachineStatsHour[] => dtos.map(toMachineStatsHour);

export const toMachineStatsDay = (dto: MachineStatsDayDto): MachineStatsDay => dto;
export const toMachineStatsDays = (dtos: MachineStatsDayDto[]): MachineStatsDay[] => dtos.map(toMachineStatsDay);

export const toMachineStatsMonth = (dto: MachineStatsMonthDto): MachineStatsMonth => dto;
export const toMachineStatsMonths = (dtos: MachineStatsMonthDto[]): MachineStatsMonth[] =>
  dtos.map(toMachineStatsMonth);

export const toMachineStatsWeek = (dto: MachineStatsWeekDto): MachineStatsWeek => dto;
export const toMachineStatsWeeks = (dtos: MachineStatsWeekDto[]): MachineStatsWeek[] => dtos.map(toMachineStatsWeek);

export const toMachineStatsHourAll = (dto: MachineStatsHourAllDto): MachineStatsHourAll => dto;
export const toMachineStatsHourAlls = (dtos: MachineStatsHourAllDto[]): MachineStatsHourAll[] =>
  dtos.map(toMachineStatsHourAll);

export const toMachineStatsDayAll = (dto: MachineStatsDayAllDto): MachineStatsDayAll => dto;
export const toMachineStatsDayAlls = (dtos: MachineStatsDayAllDto[]): MachineStatsDayAll[] =>
  dtos.map(toMachineStatsDayAll);

export const toMachineStatsMonthAll = (dto: MachineStatsMonthAllDto): MachineStatsMonthAll => dto;
export const toMachineStatsMonthAlls = (dtos: MachineStatsMonthAllDto[]): MachineStatsMonthAll[] =>
  dtos.map(toMachineStatsMonthAll);

export const toMachineStatsWeekAll = (dto: MachineStatsWeekAllDto): MachineStatsWeekAll => dto;
export const toMachineStatsWeekAlls = (dtos: MachineStatsWeekAllDto[]): MachineStatsWeekAll[] =>
  dtos.map(toMachineStatsWeekAll);

export const toNcFileStatsHour = (dto: NcFileStatsHourDto): NcFileStatsHour => dto;
export const toNcFileStatsHours = (dtos: NcFileStatsHourDto[]): NcFileStatsHour[] => dtos.map(toNcFileStatsHour);

export const toNcFileStatsDay = (dto: NcFileStatsDayDto): NcFileStatsDay => dto;
export const toNcFileStatsDays = (dtos: NcFileStatsDayDto[]): NcFileStatsDay[] => dtos.map(toNcFileStatsDay);

export const toNcFileStatsMonth = (dto: NcFileStatsMonthDto): NcFileStatsMonth => dto;
export const toNcFileStatsMonths = (dtos: NcFileStatsMonthDto[]): NcFileStatsMonth[] => dtos.map(toNcFileStatsMonth);

// Response mappers (identity for 1:1)
export const toAvailableMachinesResponse = (dto: AvailableMachinesResponseDto): AvailableMachinesResponse => dto;
export const toConfigureOperationResponse = (dto: ConfigureOperationResponseDto): ConfigureOperationResponse => dto;
export const toConfigureMachineGroupResponse = (dto: ConfigureMachineGroupResponseDto): ConfigureMachineGroupResponse =>
  dto;
export const toMachineReferencesResponse = (dto: MachineReferencesResponseDto): MachineReferencesResponse => dto;
export const toConfigureMachineReferencesResponse = (
  dto: ConfigureMachineReferencesResponseDto
): ConfigureMachineReferencesResponse => dto;
export const toRetrieveRawMachineEventDataResponse = (
  dto: RetrieveRawMachineEventDataResponseDto
): RetrieveRawMachineEventDataResponse => dto;

export const toMachineCountingProfile = (dto: MachineCountingProfileDto): MachineCountingProfile => dto;
export const toMachineCountingProfiles = (dtos: MachineCountingProfileDto[]): MachineCountingProfile[] =>
  dtos.map(toMachineCountingProfile);
